import {
  keyboardNavigable,
  nonRollingControlMode,
} from '../mixins/keyboard-navigable';

/**
 * Possible linkmodes
 */
const linkModes = {
  /**
   * If a minimum field value is greater than or equal as maximum field then the maximun field is emptied.
   * This also works otherway around.
   */
  minmax() {
    if( this._options.fieldtype === 'min' ) {
      if( parseFloat( this.value ) >= parseFloat( this._partner.value ) ) {
        this.partner.value = '';
      }
    }
    if( this._options.fieldtype === 'max' ) {
      if( parseFloat( this.value ) <= parseFloat( this._partner.value ) ) {
        this.partner.value = '';
      }
    }
  }
}

/**
 * Search mode
 */
const searchMode = {

  /**
   * Default simple search
   * @param {string} searchValue user's search input
   * @param {HTMLElement[]} options option HTMLElement object in an array
   * @returns {object[]} matching options
   */
  defaultSearch(searchValue, options) {
    return options.filter( option => {
      return String( option.dataset.value ).startsWith( searchValue );
    });
  },

  /**
   * Search result by combination of order of magnitude and rounding.
   * User inputs 4600 it's rounded to 5000, or if user inputs 34 it's rounded 30 and so and so on.
   * Rounding works big or small numbers.
   *
   * @param {string} searchValue user's search input
   * @param {HTMLElement[]} options option HTMLElement object in an array
   * @returns {object[]} matching options
   */
  searchOrderByMagnitude(searchValue, options) {

    const magnitude = Math.floor(Math.log(searchValue) / Math.LN10 + 0.000000001);
    const value = Math.pow(10,magnitude);
    const magnitudeRoundedValue = Math.round(searchValue/value)*value;

    return options.filter( option => {
      return String( option.dataset.value ).startsWith( magnitudeRoundedValue );
    });
  }
}

/**
 * PredictingInputField
 *
 * This class works with HTML printed with cm-scriptlet://mustache-predicting-input-field/.
 * More about it here https://laakkonen.stage.crasman.fi/admin/manage/scriptlets/scriptleteditor/html/code/show/n6CK3z7RTu2JucGuxLAtBQ
 */
class PredictingInputField {

  /**
   * @constructor
   * @param {HTMLElement} element element
   * @param {object} [options] control options
   * @param {string} [options.linkmode] way two input fields are tied together, linkmode function name
   * @param {string} [options.fieldtype] allowed values are 'min' or 'max'
   * @param {HTMLElement} [options.container] container element
   * @throws {TypeError} if given options.linkmode is not allowed one
   * @throws {TypeError} if given options.linkmode is used but no allowed options.fieldtype is not supplied
   */
  constructor( element, options={} ) {
    this._element = element;
    this._options = options;
    this._elements();
    this.elements.input.predictingInputField = this;
    this._bindEventListeners();
    this.bindKeyboardNavitable(element);
    this.setKeyboarGroupElement(element);

    if( this._options.linkmode ) {
      if( !Object.keys( linkModes ).includes(this._options.linkmode) ) {
        throw new TypeError(`Invalid linkmode supplied, supplied value was ${this._options.linkmode}. Allowed modes are ${Object.keys( linkModes ).join(',')}`);
      }

      if( !['min', 'max'].includes( this._options.fieldtype) ) {
        throw new TypeError(`Invalid fieldtype supplied, supplied value was ${this._options.fieldtype}. Allowed modes are 'min' or 'max'`);
      }

      this._linkMode = linkModes[this._options.linkmode];
    }
  }

  /**
   * Return field id
   * @return {string} return field id
   */
  get id() {
    return this.elements.input.id;
  }

  /**
   * Set partner input field. _partner field is used when a two fields interact together
   * like in a case of min & max fields.
   * @param {PredictingInputField} predictingInputField predicting input field
   * @throw {TypeError} when supplied value is not a instance of PredictingInputField class
   */
  set partner( predictingInputField ) {
    if( !(predictingInputField instanceof PredictingInputField) ) {
      throw new TypeError('Supplied value is not a instance of PredictingInputField')
    }

    this._partner = predictingInputField;
  }

  /**
   * If two instances of PredictingInputField works together as a pair this
   * returns field's partner instance.
   * @return {PredictingInputField} partner
   */
  get partner() {
    return this._partner;
  }

  /**
   * Set value to input element.
   * If given value is an empty string value is emptied and linkMode function is executed.
   * @param {string} value - value to be set
   * @throws {Error} if value is not one existing options
   */
  set value( value ) {

    if( !(typeof value === 'string' && value.length === 0) &&
       !Array.from(this.elements.options).find( option => option.dataset.value === value )) {
      throw new Error(`Given value don't exist in options, can not set the value! Given value was ${value}`)
    }

    const fireEvent = this.elements.input.value !== value;

    this.elements.input.value = value;

    if(fireEvent) {
      this.elements.input.dispatchEvent(new CustomEvent('predictingInputChange'));
    }

    if( value !== '' && typeof this._linkMode === 'function' ) {
      this._linkMode();
    }
  }

  /**
   * Return internal input element value
   * @returns {string} input element value
   */
  get value() {
    return this.elements.input.value;
  }

  /**
   * Makes element in the PredictingInputField accessible later
   */
  _elements() {
    this.elements = {
      input: this._element.querySelector('input'),
      inputFieldHolder: this._element.querySelector('.predicting-input-field-holder'),
      optionsHolder: this._element.querySelector('.predicting-input-field-options'),
      valueDisplay: this._element.querySelector('.redicting-input-field-value-display'),
      options: this._element.querySelectorAll('.predicting-input-field-options span.option'),
      errorMessage: this._element.querySelector('.predicting-input-field-validation-message')
    };
    this._updateFocusableElement();
  }

  /**
   * Bind event listeners
   */
  _bindEventListeners() {

    this.elements.input.addEventListener('input', (event) => {
      this._doSearch(event.target.value);
    });

    this.elements.input.addEventListener('focus', this._onFocused.bind(this));
    window.addEventListener('click', event => {
      if (!event.target.closest('.predicting-input-field')) {
        this._closeComponent();
        if( !this.validateSearchValue(this.value) || !this._isOneOfOptionsValues(this.value)) {
          this._emptyValue();
        }
      }
    });
    this.elements.optionsHolder.addEventListener( 'click', this._onOptionClicked.bind(this) );

    //This is called when a predicting input pair loses it's focus
    this._options.container.addEventListener('focusout', event => {
      if(!this._options.container.contains(event.relatedTarget) ) {
        PredictingInputField.closeComponents(this);
      }
    });
  }

  /**
   * Tabbing out
   */
  _tabOut() {
    if( !this.validateSearchValue(this.value) || !this._isOneOfOptionsValues(this.value)) {
      this._emptyValue();
    }
  }

  /**
   * Empty value and hide validation errors
   */
  _emptyValue() {
    this.value = '';
    this._showValidationErrors(true);
  }

  /**
   * Check is a value one of the options
   * @param {string} value typically user inputted value
   * @return {boolean} true if value is one of options otherwise false
   */
  _isOneOfOptionsValues(value) {
    const options =  Array.from(this.elements.options);
    return options.some( option => option.dataset.value === value );
  }

  /**
   * Update all keyboard focusabe elements. This is purely used for make component keyboardnavigable.
   */
  _updateFocusableElement() {
    this.focusableElements = Array.from( this._element.querySelectorAll('input, span.option.show') );
  }

  /**
   * Show or hide field options
   * @param {boolean} show true if options are shown false if they are hidden
   */
  _showOptions(show) {
    this.elements.optionsHolder.classList.toggle('show', show );
  }

  /**
   * Close field options
   */
  _closeComponent() {
    this._showOptions(false);
  }

  // --- Event handlers ---

  /**
   * Called when the field is focused
   */
  _onFocused() {
    this._onFocus();
    if( this.validateSearchValue(this.value) ) {
      this.elements.options.forEach( option => {
        option.classList.toggle('show', true);
      });
      this.elements.optionsHolder.classList.toggle('show', true );
    }

    PredictingInputField.closeComponents(this);
  }

  /**
   * On option is clicked
   * @param {Event} event event object
   */
  _onOptionClicked(event) {
    if( event.target.nodeName === 'SPAN' && event.target.classList.contains('option') ) {
      this.value = event.target.dataset.value;
      this.elements.input.classList.toggle('black', this.elements.input.value.length > 0);
      this.setFocusedElement(event.target);
      this._closeComponent();
    }
  }

  /**
   * On enter pressed
   * @param {HTMLElement} focusedElement currently focused element
   */
  _onEnterPressed( focusedElement ) {
    if( !this.validateSearchValue(this.value) ) {
      return;
    }
    if( focusedElement === this.elements.input ) {

      if( this.elements.input.value === '' ) {
        this.elements.input.dispatchEvent(new CustomEvent('predictingInputChange'));
      } else {
        const option = Array.from( this.elements.options ).find( option => option.classList.contains('show') );
        if( option ) {
          this.value = option.dataset.value;
        }
      }
    } else if( focusedElement.tagName === 'SPAN' && focusedElement.dataset.value ) {
      this.value = focusedElement.dataset.value;
    }

    this._closeComponent();
    this.setFocusedElement( this.focusableElements[0] );//Return focus to input element
  }

  /**
   * Validate that search value is numeric value.
   * @public
   * @param {string} searchValue numeric value
   * @return {boolean} return true is value is numberic false if not
   */
  validateSearchValue( searchValue ) {
    let value;

    if( typeof searchValue === 'string') {
      value = parseFloat( searchValue.replace(',', '.') )
    } else if( typeof searchValue === 'number' ) {
      value = searchValue;
    }

    return searchValue === '' || typeof value === 'number' && !isNaN( value );
  }

  /**
   * Called when a user type someting to the field
   * @param {string} searchValue input event
   */
  _doSearch( searchValue ) {

    let isValid = this.validateSearchValue(searchValue);

    if( isValid ) {

      const options =  Array.from(this.elements.options);
      const results = this._searchOptions( searchValue, options);

      //Show or hide options
      options.forEach( option => {
        option.classList.toggle('show', Boolean(results.find( foundOption => foundOption === option )));
      });

      this._showOptions(results.length > 0);
      this.elements.input.classList.toggle('black', this.elements.input.value.length > 0);
      this._updateFocusableElement();

      isValid = results.length > 0;
    }

    this._showValidationErrors(isValid);
  }

  /**
   * Search options
   * @param {string} searchValue search value from input field
   * @param {object} options options
   * @returns {object[]} search results
   */
  _searchOptions( searchValue, options ) {

    let results = searchMode.defaultSearch(searchValue, options);

    if( options.length > results.length ) {
      results = [ ...results, ...searchMode.searchOrderByMagnitude(searchValue, options) ];
    }

    return results;
  }

  /**
   * Show or hide validation errors
   * @param {boolean} isValid true if search value is valid false otherwise
   */
  _showValidationErrors(isValid) {
    this._element.classList.toggle('error', !isValid );
    if(!isValid) {
      this._showOptions(false);
    }
  }

  /**
   * @static
   * @property {PredictingInputField[]} instances - all existing instances of PredictingInputField in DOM are stored here.
   * This is used to link min / max functionalities
   */
  static instances = [];

  /**
   * Close all predicting input fields
   * @param {PredictingInputField} [closingInstance] this instance _closeComponent() function is not called
   */
  static closeComponents( closingInstance ) {
    PredictingInputField.instances.filter( instance => instance !== closingInstance )
      .forEach( instance => {
        instance._closeComponent();
        if( !instance.validateSearchValue(instance.value) || !instance._isOneOfOptionsValues(instance.value)) {
          instance._emptyValue();
        }
      });
  }

  /**
   * Called to initialize predicting input fields in the document object model
   */
  static init() {

    document.querySelectorAll('.predicting-input-field-container').forEach(containerElement => {

      containerElement.querySelectorAll('.predicting-input-field').forEach(element => {
        const options = {
          linkmode: (element.dataset.min || element.dataset.max ) ? 'minmax' : '',
          fieldtype: element.dataset.min ? 'max' : element.dataset.max ? 'min' : false,
          container: containerElement
        }

        PredictingInputField.instances.push(new PredictingInputField(element, options));
      });

      //Link predicting input fields together
      PredictingInputField.instances.forEach( instance => {
        const partnerId = instance._element.dataset.min ?? instance._element.dataset.max;
        const partner = PredictingInputField.instances.find( instance => instance.id === partnerId);
        instance.partner = partner;
      });
    });

  }
}

//Make component keyboard navigable
Object.assign( PredictingInputField.prototype, keyboardNavigable, nonRollingControlMode );

export {
  PredictingInputField
}
